import { stringify } from 'query-string';
import {
  fetchUtils,
} from 'ra-core';

const getPaginationQuery = (pagination) => {
  return {
    page: pagination.page,
    page_size: pagination.perPage,
  };
};

const getFilterQuery = (filter) => {
  const { q: search, ...otherSearchParams } = filter;
  return {
    ...otherSearchParams,
    search,
  };
};

export const getOrderingQuery = (sort) => {
  const { field, order } = sort;
  return {
    ordering: `${order === 'ASC' ? '' : '-'}${field}`,
  };
};

export default (
  apiUrl,
  httpClient = fetchUtils.fetchJson
) => {
  const getOneJson = (resource, id) =>
    httpClient(`${apiUrl}/${resource}/${id}/`).then(
      (response) => response.json
    );

  return {
    getList: async (resource, params) => {
      const query = {
        ...getFilterQuery(params.filter),
        ...getPaginationQuery(params.pagination),
        ...getOrderingQuery(params.sort),
      };
      // Normalize ordering fields per resource if needed
      if (resource === 'activities' && typeof query.ordering === 'string') {
        query.ordering = query.ordering.replace('track_title', 'track');
      }
      const url = `${apiUrl}/${resource}/?${stringify(query)}`;

      const { json } = await httpClient(url);

      return {
        data: json.results,
        total: json.count,
      };
    },

    getOne: async (resource, params) => {
      const data = await getOneJson(resource, params.id);
      return {
        data,
      };
    },

    getMany: async (resource, params) => {
      if (!params.ids || params.ids.length === 0) {
        return { data: [] };
      }
      // Users and sites lists may be restricted; prefer per-ID fetch to avoid 403
      const preferPerId = resource === 'users' || resource === 'sites';
      if (!preferPerId) {
        // Prefer a single id__in query to avoid per-ID 404s
        const query = {
          id__in: params.ids.join(','),
          page_size: params.ids.length,
        };
        const url = `${apiUrl}/${resource}/?${stringify(query)}`;
        try {
          const { json } = await httpClient(url);
          return { data: json.results };
        } catch (e) {
          // Fall through to per-ID fetch
        }
      }
      // Best-effort per-ID fetch, tolerant to 404/403 per item
      const results = await Promise.allSettled(
        params.ids.map(id => getOneJson(resource, id))
      );
      const data = results
        .filter(r => r.status === 'fulfilled')
        .map(r => r.value);
      return { data };
    },

    getManyReference: async (resource, params) => {
      const query = {
        ...getFilterQuery(params.filter),
        ...getPaginationQuery(params.pagination),
        ...getOrderingQuery(params.sort),
        [params.target]: params.id,
      };
      if (resource === 'activities' && typeof query.ordering === 'string') {
        query.ordering = query.ordering.replace('track_title', 'track');
      }
      const url = `${apiUrl}/${resource}/?${stringify(query)}`;

      const { json } = await httpClient(url);
      return {
        data: json.results,
        total: json.count,
      };
    },

    update: async (resource, params) => {
      // Transform overlay field for tracks to send only the ID
      let data = params.data;
      if (resource === 'tracks' && data.overlay && typeof data.overlay === 'object' && data.overlay.id) {
        data = { ...data, overlay: data.overlay.id };
      }
      
      const { json } = await httpClient(`${apiUrl}/${resource}/${params.id}/`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return { data: json };
    },

    updateMany: (resource, params) => {
      // Transform overlay field for tracks to send only the ID
      let data = params.data;
      if (resource === 'tracks' && data.overlay && typeof data.overlay === 'object' && data.overlay.id) {
        data = { ...data, overlay: data.overlay.id };
      }
      
      return Promise.all(
        params.ids.map(id =>
          httpClient(`${apiUrl}/${resource}/${id}/`, {
            method: 'PUT',
            body: JSON.stringify(data),
          })
        )
      ).then(responses => ({ data: responses.map(({ json }) => json.id) }));
    },

    create: async (resource, params) => {
      // Transform overlay field for tracks to send only the ID
      let data = params.data;
      if (resource === 'tracks' && data.overlay && typeof data.overlay === 'object' && data.overlay.id) {
        data = { ...data, overlay: data.overlay.id };
      }
      
      const { json } = await httpClient(`${apiUrl}/${resource}/`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return {
        data: { ...json },
      };
    },

    delete: (resource, params) =>
      httpClient(`${apiUrl}/${resource}/${params.id}/`, {
        method: 'DELETE',
      }).then(() => ({ data: params.previousData })),

    deleteMany: (resource, params) =>
      Promise.all(
        params.ids.map(id =>
          httpClient(`${apiUrl}/${resource}/${id}/`, {
            method: 'DELETE',
          })
        )
      ).then(responses => ({ data: responses.map(({ json }) => json.id) })),
  };
};